import { GrowingSpacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";

import { FaireAndBrandLogo } from "./FaireAndBrandLogo";
import { SignUpButton } from "./SignUpButton";

interface IProps {
  showNav: boolean;
  eventRoot: string;
  hideInput?: boolean;
  brandName: string;
}

const animationLength = 300;
const navHeightDesktop = 80;
const navHeightMobile = 80;

export const SignUpNavComponent: React.FC<IProps> = (props) => {
  const { isMobile } = useViewport();
  const [animating, setAnimating] = React.useState(false);
  const prevProps = useRef<IProps>(props);
  const timeout = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prevProps.current.showNav && !props.showNav) {
      setAnimating(true);
      timeout.current = setTimeoutIfPossible(() => {
        setAnimating(false);
      }, animationLength);
    }

    prevProps.current = props;
  }, [props]);

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current);
    };
  }, []);

  const { showNav, hideInput, brandName } = props;
  if (!showNav && !animating) {
    return null;
  }
  const Nav = showNav ? AppearingNav : DisappearingNav;

  return (
    <Nav hideInput={hideInput}>
      {!isMobile ? <FaireAndBrandLogo brandName={brandName} /> : null}
      <GrowingSpacer />
      <SignUpButton primary />
    </Nav>
  );
};

const SignUpNav = styled.nav<{ $hideInput?: boolean }>`
  position: fixed;
  top: 0;
  height: ${navHeightDesktop}px;
  width: 100%;
  background-color: #f9f5f0;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  z-index: 12;

  ${responsiveMediaQueries.mobile} {
    height: ${(props) =>
      props.$hideInput ? navHeightDesktop : navHeightMobile}px;
    padding: 16px 18px;
  }

  ${responsiveMediaQueries.tablet} {
    padding: 0 24px;
  }
`;

const slideIn = keyframes`
    0% {
        top: -${navHeightDesktop}px;
    }
    100% {
        top: 0;
    }
`;

const slideOut = keyframes`
    0% {
        top: 0px;
    }
    100% {
        top: -${navHeightDesktop}px;
    }
`;

const slideInMobile = keyframes`
    0% {
        top: -${navHeightMobile}px;
    }
    100% {
        top: 0;
    }
`;

const slideOutMobile = keyframes`
    0% {
        top: 0px;
    }
    100% {
        top: -${navHeightMobile}px;
    }
`;

const AppearingNav = styled(SignUpNav)<{ hideInput?: boolean }>`
  animation: ${slideIn} ${animationLength}ms ease-in-out 0s;

  ${responsiveMediaQueries.mobile} {
    animation: ${(props) => (props.hideInput ? slideIn : slideInMobile)}
      ${animationLength}ms ease-in-out 0s;
  }
`;

const DisappearingNav = styled(SignUpNav)<{ hideInput?: boolean }>`
  top: -${navHeightDesktop}px;
  animation: ${slideOut} ${animationLength}ms ease-in-out 0s;
  ${responsiveMediaQueries.mobile} {
    top: -${(props) => (props.hideInput ? navHeightDesktop : navHeightMobile)}px;
    animation: ${(props) => (props.hideInput ? slideOut : slideOutMobile)}
      ${animationLength}ms ease-in-out 0s;
  }
`;
